import React, { useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import {
  MdAdd,
  MdGavel,
  MdLocalShipping,
  MdOutlineLocalOffer,
  MdPostAdd,
} from "react-icons/md";
import AppButton from "../../../../components/buyer/AppButton";
import Loader from "../../../../components/common/Loader";
import { useNavigate } from "react-router-dom";
import VerifySwitchRoleModal from "../../../../components/buyer/VerifySwitchRoleModal";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUserAddresses,
  switchRole,
} from "../../../../features/user/userSlice";
import { setCredentials } from "../../../../features/auth/authSlice";

const HeaderSection = ({ title }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token, role, roles, user } = useSelector((state) => state.auth);
  // const { roles } = useSelector((state) => state.user);
  const isLoggedIn = Boolean(token);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  const handleRoleSwitch = async (targetRole, targetPath = null) => {
    setIsSwitching(true);
    try {
      // 🚀 State synchronization (token, role, persistence) is now handled
      // automatically by authSlice.extraReducers for switchRole.fulfilled
      const result = await dispatch(switchRole({ role: targetRole })).unwrap();

      // If switching to seller, fetch address count
      if (targetRole === "seller") {
        try {
          const addressRes = await dispatch(fetchUserAddresses()).unwrap();
          const addressList = addressRes?.data || addressRes;
          const addressCount = Array.isArray(addressList)
            ? addressList.length
            : 0;

          // Re-sync: merge freshUser ON TOP of existing user to preserve verification flags
          const freshUser = result?.data?.user || result?.user;
          const updatedRoles = roles?.includes(targetRole)
            ? roles
            : [...(roles || []), targetRole];
          const mergedUser = { ...user, ...(freshUser || {}), addresses_count: addressCount, roles: updatedRoles };
          if (mergedUser) {
            dispatch(
              setCredentials({
                user: mergedUser,
                role: targetRole,
              }),
            );
          }
        } catch (addrErr) {
          console.error("Error fetching addresses during switch:", addrErr);
        }
      }

      // Navigate to the specified path, or fall back to default dashboard
      if (targetPath) {
        navigate(targetPath);
      } else if (targetRole === "seller") {
        navigate("/seller/dashboard");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Role switch failed:", error);
      setIsSwitching(false);
    }
  };

  // Handler for modal confirmation — after activating seller, go straight to create bid
  const handleConfirmRoleSwitch = () => {
    handleRoleSwitch("seller", "/seller/products");
  };

  const handleSellerRoleSwitch = async () => {
    if (!isLoggedIn) {
      navigate("/login", { state: { role: "seller" } });
      return;
    }

    // Currently logged in as seller → go to create bid page directly
    if (role === "seller") {
      navigate("/seller/products");
      return;
    }

    // Logged in as buyer but has seller role → switch role then navigate to create bid
    if (role === "buyer" && roles?.includes("seller")) {
      handleRoleSwitch("seller", "/seller/products");
      return;
    }

    // User logged in but not registered as a seller
    if (!roles?.includes("seller")) {
      setShowConfirmModal(true);
      return;
    }
  };

  return (
    <>
      {isSwitching && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Loader />
        </Box>
      )}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={6}
        sx={{
          borderBottom: "1px solid rgba(0,0,0,0.05)",
          pb: 2,
        }}
      >
        {/* LEFT SIDE: Dashboard + Buttons */}
        <Box display="flex" alignItems="center" gap={2}>
          <Typography
            variant="h4"
            fontWeight={700}
            sx={{ color: "text.primary", letterSpacing: "0.5px" }}
          >
            {title}
          </Typography>

          <AppButton
            fullWidth={false}
            variant="contained"
            onClick={() => navigate("/market?tab=bids")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 12px",
              backgroundColor: "#e8f0fe",
              color: theme.palette.info.main,
              border: `2px solid ${theme.palette.info.main}`,
            }}
          >
            <MdGavel size={18} /> Bid Now
          </AppButton>

          <AppButton
            fullWidth={false}
            variant="contained"
            onClick={() => navigate("/create-offer")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 12px",
              backgroundColor: "#fffbe7",
              color: theme.palette.warning.main,
              border: `2px solid ${theme.palette.warning.main}`,
            }}
          >
            <MdOutlineLocalOffer size={18} /> Create Offer
          </AppButton>

          <AppButton
            fullWidth={false}
            variant="contained"
            onClick={handleSellerRoleSwitch}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 12px",
              backgroundColor: "#e9fbe9",
              color: theme.palette.success.main,
              border: `2px solid ${theme.palette.success.main}`,
            }}
          >
            <MdPostAdd size={18} /> Sell Scrap – Create Bid
          </AppButton>
        </Box>

        {/* RIGHT SIDE: Date */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ opacity: 0.7 }}
        >
          {new Date().toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </Typography>
      </Box>
      <VerifySwitchRoleModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmRoleSwitch}
        title="Activate Seller Account"
        description="You are about to activate your seller account."
        confirmText="Activate"
        cancelText="Cancel"
      />
    </>
  );
};

export default HeaderSection;
